import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Select, Input, Button, notification, Divider, Avatar, Row, Col } from 'antd';
import { UserOutlined, FileTextOutlined } from '@ant-design/icons';
import { HITECH_API, getAuthHeaders } from './api';

const STATUS_COLORS = { pending: 'orange', shortlisted: 'green', rejected: 'red' };

const Field = ({ label, value }) => (
  value ? (
    <Descriptions.Item label={label}>{value}</Descriptions.Item>
  ) : null
);

const ApplicationDetail = ({ application, onClose, onUpdate }) => {
  const [status, setStatus] = useState(application?.status || 'pending');
  const [remarks, setRemarks] = useState(application?.adminRemarks || '');
  const [saving, setSaving] = useState(false);

  if (!application) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${HITECH_API}/admin/applications/${application._id}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status, adminRemarks: remarks }),
      });
      const data = await res.json();
      if (data.success) {
        notification.success({ message: 'Status updated successfully' });
        onUpdate({ ...application, status, adminRemarks: remarks });
      } else {
        notification.error({ message: data.message || 'Update failed' });
      }
    } catch {
      notification.error({ message: 'Network error' });
    } finally {
      setSaving(false);
    }
  };

  const relevantExp = Array.isArray(application.relevantExperience)
    ? application.relevantExperience.map(obj => {
        const entries = obj instanceof Map
          ? Array.from(obj.entries())
          : Object.entries(obj || {});
        return entries.map(([k, v]) => `${k}: ${v} yrs`).join(', ');
      }).filter(Boolean).join(' | ')
    : '';

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={820}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={40} src={application.photo} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#0d2e61' }}>{application.name || '—'}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{application.positionAppliedFor} · {application.departmentAppliedFor}</div>
          </div>
          <Tag color={STATUS_COLORS[application.status] || 'default'} style={{ marginLeft: 'auto', textTransform: 'capitalize', fontSize: 13 }}>
            {application.status || 'pending'}
          </Tag>
        </div>
      }
    >
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small" style={{ marginBottom: 16 }}>
        <Field label="Full Name" value={application.name} />
        <Field label="Gender" value={application.gender} />
        <Field label="Date of Birth" value={application.dob ? new Date(application.dob).toLocaleDateString('en-IN') : null} />
        <Field label="Contact" value={application.contactNumber} />
        <Field label="Alt. Contact" value={application.alternateContactNumber} />
        <Field label="Email" value={application.emailId} />
        <Field label="Department" value={application.departmentAppliedFor} />
        <Field label="Position Applied" value={application.positionAppliedFor} />
        <Field label="Fresher" value={application.fresher} />
        <Field label="Current Company" value={application.currentCompanyName} />
        <Field label="Current Designation" value={application.currentDesignation} />
        <Field label="Total Experience" value={application.totalExperience} />
        <Field label="Current Location" value={application.currentLocation} />
        <Field label="Home Location" value={application.home} />
        <Field label="Current CTC" value={application.currentCTC} />
        <Field label="Expected CTC" value={application.expectedCTC} />
        <Field label="Notice Period" value={application.noticePeriod} />
        <Field label="Qualification" value={application.qualification} />
        <Field label="Skills" value={application.skill} />
        <Field label="Reference" value={application.reference} />
        <Field label="Referred By" value={application.referenceOfFriend} />
        <Field label="Reference (Others)" value={application.referenceOfOthers} />
        {relevantExp && <Field label="Relevant Experience" value={relevantExp} />}
        {application.remarks && <Field label="Applicant Remarks" value={application.remarks} />}
        <Descriptions.Item label="Applied On" span={2}>
          {new Date(application.createdAt).toLocaleString('en-IN')}
        </Descriptions.Item>
      </Descriptions>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        {application.resume && (
          <Col>
            <Button icon={<FileTextOutlined />} href={application.resume} target="_blank" rel="noreferrer">
              View Resume
            </Button>
          </Col>
        )}
        {application.photo && (
          <Col>
            <Button href={application.photo} target="_blank" rel="noreferrer" icon={<UserOutlined />}>
              View Photo
            </Button>
          </Col>
        )}
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      <div style={{ fontWeight: 600, marginBottom: 8, color: '#0d2e61' }}>Update Application Status</div>
      <Row gutter={12} align="middle">
        <Col xs={24} sm={8}>
          <Select value={status} onChange={setStatus} style={{ width: '100%' }} size="large">
            <Select.Option value="pending"><Tag color="orange">Pending</Tag></Select.Option>
            <Select.Option value="shortlisted"><Tag color="green">Shortlisted</Tag></Select.Option>
            <Select.Option value="rejected"><Tag color="red">Rejected</Tag></Select.Option>
          </Select>
        </Col>
        <Col xs={24} sm={12}>
          <Input
            placeholder="Admin remarks (optional)"
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            size="large"
          />
        </Col>
        <Col xs={24} sm={4}>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            size="large"
            style={{ background: '#0d2e61', borderColor: '#0d2e61', width: '100%' }}
          >
            Save
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default ApplicationDetail;
